import { useCallback, useMemo } from 'react';
import { t } from '@lingui/macro';

import useDebouncedValue from 'hooks/useDebouncedValue';

import type { SearchableFormValues } from 'components/SearchableFilter';
import { useTeamsFiltersQuery } from '../useTeamsFiltersQuery';

interface UseTeamFiltersProps {
  value: string[];
  onSubmit: (data: SearchableFormValues) => void;
}

export default function useTeamsFilters({
  value,
  onSubmit,
}: UseTeamFiltersProps) {
  const [searchValue, setSearchString, debouncedSearchValue] =
    useDebouncedValue('');

  const {
    data: { managedSentinelTeams: { collection = [] } = {} } = {},
    loading,
    loadMore,
    loadingMore,
  } = useTeamsFiltersQuery({
    variables: { page: 1, text: debouncedSearchValue },
  });

  const onSearch = useCallback(
    (val: string) => {
      setSearchString(val);
    },
    [setSearchString],
  );

  const items = useMemo(
    () =>
      collection.map((record) => ({
        id: record.id,
        label: record.name,
        value: record.id,
      })),
    [collection],
  );

  const text = useMemo(() => {
    if (value.length === 0) return t`All Teams`;

    if (value.length === 1)
      return items.find((item) => item.value === value[0])?.label || t`Team`;

    return t`Teams (${value.length})`;
  }, [items, value]);

  return {
    items,
    text,
    loading,
    searchValue,
    debouncedSearchValue,
    onSearch,
    mode: 'async' as const,
    value,
    onSubmit,
    onLoadMore: loadMore,
    loadingMore,
  };
}
